import type { LicenseOrigin } from '@/generated/http-clients/v3ApiSchemas'
import { Badge, type BadgeProps } from '@chakra-ui/react'

/**
 * Colour by who drove the licence: pink for customer-driven motions, purple
 * for HiveMQ-driven ones, grey for internal-only so it never reads as a real
 * customer. Deliberately a different hue family from `LicenseStatusBadge`'s
 * green / grey / red, so the two pills in adjacent columns don't blur into
 * one signal.
 *
 * This groups rather than uniquely identifies — the deal-driven motions share
 * a colour and are told apart by their label.
 *
 * Typed as a total map, so adding an origin to the API contract fails the
 * build here rather than silently rendering grey.
 */
const ORIGIN_COLORS: Record<LicenseOrigin, string> = {
  'self-service': 'pink',
  marketplace: 'pink',
  'sales-led': 'purple',
  'partner-led': 'purple',
  internal: 'gray',
}

/**
 * Colour for an origin this client does not know: one the backend added ahead
 * of a client release, or one it removed that older licences still carry
 * (`staff-led`, dropped by hivemq-cloud PR #7086).
 *
 * Deliberately NOT `gray`. Grey is this map's "internal — never reads as a real
 * customer" signal, so falling back to it would make a genuine customer licence
 * mis-signal as internal. Orange reads as "unrecognised, look closer", which is
 * what an unknown origin actually means.
 */
const UNKNOWN_ORIGIN_COLOR = 'orange'

function originColor(origin: LicenseOrigin): string {
  return ORIGIN_COLORS[origin] ?? UNKNOWN_ORIGIN_COLOR
}

type Props = {
  origin: LicenseOrigin
} & Omit<BadgeProps, 'colorPalette' | 'children'>

/**
 * The licence origin pill — the business motion that produced the licence.
 *
 * Single source of the origin → colour mapping, shared by the licences table
 * and the detail page title, the same contract `LicenseStatusBadge` follows.
 * Presentation props pass through, so the table keeps the small default while
 * the detail page can size it up.
 */
export function LicenseOriginBadge({ origin, ...badge }: Readonly<Props>) {
  return (
    <Badge
      variant="subtle"
      {...badge}
      colorPalette={originColor(origin)}
      textTransform="capitalize"
    >
      {origin}
    </Badge>
  )
}
